<script setup lang="ts">
    import { ref, computed, watch, onMounted } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import DateTimePicker from '@shared/components/DateTimePicker/DateTimePicker.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormSelect from '@shared/components/Form/FormSelect.vue';
    import type { Issue } from '../../interfaces';
    import * as statsApi from '../IssueStatistics.access';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';

    const props = defineProps<{
        show: boolean;
        issue: Issue;
    }>();

    const emit = defineEmits<{
        (e: 'close'): void;
    }>();

    const title = ref('');
    const description = ref('');
    const scheduleDate = ref<string | null>(null);
    const scheduleHour = ref(0);
    const scheduleMinute = ref(0);
    const schedule = computed(() => {
        if (!scheduleDate.value) return '';
        const h = scheduleHour.value.toString().padStart(2, '0');
        const m = scheduleMinute.value.toString().padStart(2, '0');
        return `${scheduleDate.value}T${h}:${m}:00`;
    });
    const timeZones = ref<{ TimeZoneId: string; Name: string }[]>([]);
    const selectedTimeZone = ref('');
    const saving = ref(false);

    watch(
        () => props.issue,
        (issue) => {
            if (issue) title.value = issue.Subject;
        },
        { immediate: true }
    );

    onMounted(async () => {
        const [zones, defaultZone] = await Promise.all([statsApi.getTimeZones(), statsApi.getDefaultTimeZone()]);
        timeZones.value = zones;
        selectedTimeZone.value = defaultZone;
    });

    async function save() {
        if (!title.value || !schedule.value) {
            bsAlert({ type: AlertTypes.Danger, content: $Resources.reminderModelAlertError });
            return;
        }
        saving.value = true;
        try {
            await statsApi.scheduleReminder({
                issueId: props.issue.IssueId,
                subject: title.value,
                description: description.value,
                schedule: schedule.value,
                timeZone: selectedTimeZone.value,
            });
            bsAlert({ type: AlertTypes.Success, content: $Resources.reminderModelAlertSuccess });
            emit('close');
        } catch {
            bsAlert({ type: AlertTypes.Danger, content: $Resources.reminderModelAlertError });
        } finally {
            saving.value = false;
        }
    }
</script>

<template>
    <Modal :show="show" :title="$Resources.ReminderModelHeading" @close="emit('close')">
        <template #content>
            <p>{{ $Resources.ReminderModelBody }}</p>

            <FormInput
                class="form-group"
                :label="$Resources.ReminderModelTitleLabel"
                :placeholder="issue.Subject"
                :modelValue="title"
                :required="true"
                @update:modelValue="title = $event" />

            <FormInput
                class="form-group"
                :label="$Resources.ReminderModelDescriptionLabel"
                :placeholder="$Resources.ReminderModelDescriptionPlaceholder"
                :modelValue="description"
                @update:modelValue="description = $event" />

            <div class="row">
                <div class="col-xs-12 col-md-6">
                    <DateTimePicker class="form-group" type="date" :clearable="true" :startEmpty="true" v-model:value="scheduleDate" />
                </div>
                <div class="col-xs-12 col-md-6">
                    <DateTimePicker class="form-group" type="time" v-model:value_hour="scheduleHour" v-model:value_minute="scheduleMinute" />
                </div>
            </div>

            <FormSelect
                class="form-group"
                :label="$Resources.GenericTimeZone"
                :modelValue="selectedTimeZone"
                :options="timeZones.map((tz) => ({ value: tz.TimeZoneId, label: tz.Name }))"
                @update:modelValue="selectedTimeZone = $event" />
        </template>
        <template #button>
            <button class="btn btn-primary" :disabled="saving" @click="save">
                {{ $Resources.ReminderModelCreateReminder }}
            </button>
        </template>
    </Modal>
</template>

